import type { FeatureSummary } from "@tdelivr/api-client/generated/configuration/feature-management.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the platform flags board does to the summaries CFG-02 returns, away from any rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `pflags`, against CFG-02
 * `GET /v1/platform/features` — published by
 * [ADR 0053](../../../docs/adr/0053-a-platform-scoped-flag-summary-listing-is-a-separate-operation-and-a-separate-scope.md)
 * because the only public listing this service had was "scoped to the authenticated tenant", and a
 * page titled **Platform** flags may not be drawn from a tenant-bounded listing (ADR 0048).
 *
 * ```
 * pflags:['Platform flags','CFG-02','Capabilities switched on per tenant or platform-wide.',
 *   [['Flags','38','done',''],['Per tenant','14','active',''],
 *    ['Kill switches','4','lock','armed'],['Stale','2','caution','']],
 *   ['Flag','Scope','State'],
 *   [['commerce_module','ten_7Q2M, ten_3KD9','on for 2 of 5','active','partial'],
 *    ['payments.kill_switch.airtel','platform','off (armed)','lock','armed'],
 *    ['new_search_ranker','platform','10%','active','rolling out'],
 *    ['legacy_webhooks_v1','platform','on · expires 1 Nov','caution','remove soon']],
 *   'warn','Kill switches take effect in under 30 seconds and page the owning team.']
 * ```
 *
 * ## One row per flag key, which is the design's row and not the listing's
 *
 * **The listing answers one row per `(tenantId, flagKey)` and the design draws one row per flag**,
 * with the tenants that hold it in the `Scope` column and a fraction in the `State` column. So this
 * module groups. That is not a liberty taken with the design: `commerce_module · ten_7Q2M, ten_3KD9`
 * is a single row of the design naming two tenants, and a board that drew two rows would be drawing
 * CFG-02's aggregate where the design draws the platform's.
 *
 * The grouping also decides the fraction, and this is the part worth reading carefully.
 * **`on for 2 of 5` means "on for 2 of the 5 tenants that hold this flag", not "of the 5 tenants on
 * the platform".** The second is unsayable here and the reason is the sibling page's: nothing
 * publishes how many tenants exist — `POST /v1/tenants` exists and there is no `GET`, which is why
 * `Tenant list and provisioning` beside this one is blocked — and `platform-audit.ts`'s
 * `tenantSpanNote` already took that decision in those words, writing "records from 3 tenants"
 * rather than "3 of 7". The denominator used here needs no roster: it is the number of rows in the
 * group, which the listing answered. `flagScopeNote` says so under the grid rather than leaving an
 * operator to assume the stronger reading, and **Q-189** records the difference.
 *
 * ## `platform` is never written in the `Scope` column, and that is the model rather than a gap
 *
 * Two of the design's four rows read `platform` in that column, meaning a flag that is not per
 * tenant. **CFG-02 models no such flag.** `feature_versions` has `tenant_id uuid NOT NULL` in its
 * primary key, every write is addressed `/internal/v1/tenants/{tenantId}/features/{flagKey}`, and
 * `evaluateFeature` hashes the tenant into the rollout bucket. A platform-wide flag would be a new
 * aggregate, not a null column. So this column lists the tenants that hold the flag, always, and
 * **Q-190** records the design's second grammar as unmodelled rather than unpublished.
 *
 * ## The state is read off CFG-02's own evaluation order, not off a status field
 *
 * There is no status: `state` is `"published"` on every row and ADR 0045 §2 withholds it for that
 * reason. What decides whether a flag is on is `evaluateFeature`, in a fixed order — the kill
 * switch, then the first matching targeting rule, then the percentage rollout when it is above
 * zero, then `defaultEnabled` — and `perTenantState` below is that order read back. It is
 * `apps/tenant-admin-console/src/flags.ts`'s reading, deliberately the same rather than a second
 * opinion: two consoles disagreeing about whether a flag is on would be worse than either being
 * wrong. The two readings that look surprising are the two that matter:
 *
 *   - **`0%` does not mean off.** With no percentage rollout the default decides, so a flag at zero
 *     with `defaultEnabled` is on for everyone.
 *   - **A rule can switch a flag on with the rollout at zero**, because `evaluateFeature` returns
 *     the first matching rule's own `enabled` before the rollout is consulted at all.
 *
 * ## What has no source
 *
 * **The expiry, which is one of the design's four rows, one badge word and one stat tile.**
 * `legacy_webhooks_v1 · platform · on · expires 1 Nov` is badged `caution · remove soon`, the
 * `Stale · 2` tile counts the same thing, and CFG-02 holds no expiry — a version carries a
 * description, two booleans, a rollout, a seed, its rules and its approval evidence. Nothing
 * anywhere publishes one. Q-151 is where that already lives, and this board draws neither the word
 * nor a cell for it.
 *
 * **All four stat tiles.** `Flags · 38` and `Per tenant · 14` are counts, and R-04's measurement is
 * that no collection read on this platform publishes a total, so a tile reading `12` would mean "12
 * so far" under a label meaning "38". `Kill switches · 4` and `Stale · 2` are counts of the whole
 * platform rather than of the page. Q-178 carries the tile row across this console.
 *
 * **The allocation seed is withheld and this board does not want it.** It is the value
 * `stableBucket` hashes with a subject key, so a board holding every tenant's seeds could say which
 * customers are in every tenant's 25% rollout before it reaches them. ADR 0053 is mostly about that
 * sentence.
 */

/** The design's own rollout grammar: `100%`, `25%`, `10%`, `0%`. */
const percentage = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 });

export function rolloutText(feature: FeatureSummary): string {
  return `${percentage.format(feature.rolloutBasisPoints / 100)}%`;
}

/** The targeting rules a flag is switched **on** by, which is not all of its rules. */
function includedRules(feature: FeatureSummary): FeatureSummary["targetingRules"] {
  return feature.targetingRules.filter((rule) => rule.enabled);
}

/** The targeting rules a flag is switched **off** by, which this design has no column for. */
function excludedRules(feature: FeatureSummary): FeatureSummary["targetingRules"] {
  return feature.targetingRules.filter((rule) => !rule.enabled);
}

/** What one tenant's version of a flag answers, read off `evaluateFeature`'s own order. */
export type PerTenantState = "killed" | "on" | "rolling out" | "on for an audience" | "off";

/**
 * Whether this flag is on for this tenant, in CFG-02's own order.
 *
 * Identical to `apps/tenant-admin-console/src/flags.ts`'s `stateOf` in every branch, and the
 * duplication is deliberate: `docs/adr/0021-two-consoles-and-no-shared-shell.md` is why nothing is
 * shared between channel applications yet, and a second *reading* of the same published order is
 * the thing that must not differ. `tests/platform-flags.test.ts` drives every branch.
 */
export function perTenantState(feature: FeatureSummary): PerTenantState {
  if (feature.killSwitch) return "killed";
  if (feature.rolloutBasisPoints >= 10_000) return "on";
  if (feature.rolloutBasisPoints > 0) return "rolling out";
  if (feature.defaultEnabled) return "on";
  if (includedRules(feature).length > 0) return "on for an audience";
  return "off";
}

/** Whether a tenant's flag answers true for anybody at all. */
function switchedOn(state: PerTenantState): boolean {
  return state === "on" || state === "rolling out" || state === "on for an audience";
}

/** One flag key, and every tenant's version of it this listing answered. */
export interface FlagGroup {
  readonly flagKey: string;
  /** In the order the listing answered them, which is tenant and then key. */
  readonly versions: readonly FeatureSummary[];
}

/**
 * The listing's rows as the design's rows: one per flag key, tenants in the order they arrived.
 *
 * A `Map` preserves insertion order, so a group's tenants are in the listing's own
 * `(tenant_id, flag_key)` order and the board is the same board twice. The groups themselves are
 * ordered by the first tenant that holds each flag rather than alphabetically, which is the
 * design's `Default order` — its toolbar's `Name A–Z` is what sorts by key.
 */
export function groupByFlag(features: readonly FeatureSummary[]): readonly FlagGroup[] {
  const groups = new Map<string, FeatureSummary[]>();
  for (const feature of features) {
    const held = groups.get(feature.flagKey);
    if (held === undefined) groups.set(feature.flagKey, [feature]);
    else held.push(feature);
  }
  return [...groups].map(([flagKey, versions]) => ({ flagKey, versions }));
}

/**
 * The design's `Scope` column: `ten_7Q2M, ten_3KD9`.
 *
 * The tenants that hold this flag, joined by the design's own comma and space. Never the design's
 * other value, `platform` — see this file's header, and Q-190.
 */
export function scopeText(group: FlagGroup): string {
  return group.versions.map((feature) => feature.tenantId).join(", ");
}

/**
 * The design's `State` column, in each of its three reachable grammars.
 *
 * | the design's cell       | when                                                            |
 * | ----------------------- | --------------------------------------------------------------- |
 * | `off (armed)`           | the kill switch is set on every tenant that holds the flag      |
 * | `10%`                   | every tenant is mid-rollout at the same share                   |
 * | `on for 2 of 5`         | on for some of the tenants that hold it and not all             |
 *
 * And two the design has no cell for and the data certainly has: `on` where every tenant has it on,
 * and `off` where none does. The fourth designed grammar, `on · expires 1 Nov`, needs the expiry
 * Q-151 records as absent and is not written.
 *
 * `off (armed)` is the design's own parenthetical and is kept word for word: a kill switch that is
 * set makes every evaluation answer false, and "armed" is what the design calls a flag whose switch
 * is wired and ready rather than one that has been flipped.
 */
export function stateText(group: FlagGroup): string {
  const states = group.versions.map(perTenantState);
  const held = states.length;
  if (states.every((state) => state === "killed")) return "off (armed)";

  const shares = new Set(group.versions.map(rolloutText));
  if (states.every((state) => state === "rolling out") && shares.size === 1) {
    return [...shares][0] ?? "";
  }

  const on = states.filter(switchedOn).length;
  if (on === held) return "on";
  if (on === 0) return "off";
  return `on for ${String(on)} of ${String(held)}`;
}

/**
 * The badge's class and word, which the design draws beside the cell above.
 *
 * Its own four: `active · partial`, `lock · armed`, `active · rolling out`, `caution · remove soon`.
 * Three are reachable and the fourth is Q-151's. Two more are added for the states the design has
 * no row for, in its own vocabulary: `done · on` and `closed · off`, which are the classes
 * `01 Token System v2.dc.html` gives those two words everywhere else in the product.
 *
 * **`armed` is drawn `closed` rather than `lock`, and that is a recorded divergence rather than a
 * choice.** The design's own badge kind for that row is `lock`, which is a **tenth** state class
 * that `16 WP15 Platform Machinery.dc.html` declares in its `SEMK` map — along with six other
 * work-package files — and that `01 Token System v2.dc.html` does not. `@tdelivr/design-tokens`'
 * `StateClassKey` is generated from the token file by `tooling/scripts/extract-state-classes.mjs`
 * and carries nine, so `lock` is not a class this console can name. `closed` is the nearest of the
 * nine and the nearest in the design's own terms: `SEMK.lock` and `SEMK.closed` draw the **same
 * `square` glyph** and differ only in tint — `var(--accentTint)` with `#3E63DD` against
 * `var(--muted)` with `var(--text2)`. So the shape and the word are the design's and the tint is
 * not, which is a visible difference and is recorded as **Q-193**.
 *
 * A state is never shown by colour alone — every one of these carries a glyph and a word, which
 * `StateBadge` draws from `classKey`, and `armed` and `off` are different words.
 */
export function stateOf(group: FlagGroup): {
  classKey: StateClassKey;
  label: string;
  title?: string;
} {
  const states = group.versions.map(perTenantState);
  const held = states.length;

  if (states.every((state) => state === "killed")) {
    return {
      // Q-193: the design's own class here is `lock`, which the token system does not carry.
      classKey: "closed",
      label: "armed",
      title:
        "The kill switch is set, so every evaluation answers false whatever the rollout and the targeting rules say.",
    };
  }
  if (states.every((state) => state === "rolling out")) {
    return { classKey: "active", label: "rolling out" };
  }

  const on = states.filter(switchedOn).length;
  if (on === held) {
    return {
      classKey: "done",
      label: "on",
      ...(group.versions.some((feature) => excludedRules(feature).length > 0)
        ? {
            title:
              "On for every tenant that holds it, except the subjects a disabled targeting rule matches — those are answered false before the rollout is consulted.",
          }
        : {}),
    };
  }
  if (on === 0) return { classKey: "closed", label: "off" };
  return {
    classKey: "active",
    label: "partial",
    title: `On for ${String(on)} of the ${String(held)} tenants that hold this flag. Nothing publishes how many tenants exist, so this is not a share of the platform.`,
  };
}

/** One rule as the design writes an audience: its values, joined by its own middle dot. */
function ruleText(rule: FeatureSummary["targetingRules"][number]): string {
  return rule.values.join(" · ");
}

/**
 * The audience one tenant's version of the flag names, in the tenant admin board's own grammar.
 *
 * This design has no `Audience` column — `17 WP16 Tenant Admin.dc.html`'s `flags` page does — so it
 * appears in the detail panel, per tenant, where the design's panel has room for the fields a row's
 * columns could not carry. `All` is the design's own word for a flag with no targeting, and is
 * exactly what `evaluateFeature` does when no rule matches.
 */
function audienceText(feature: FeatureSummary): string {
  const included = includedRules(feature);
  return included.length === 0 ? "All" : included.map(ruleText).join(" · ");
}

/**
 * One row of the platform flags board.
 *
 * The detail panel carries one line per tenant that holds the flag — its state, its rollout and its
 * audience — because the grid's three columns cannot, and because "which tenants, and on for which"
 * is the one question a platform operator opens this row to ask. The design's panel repeats the
 * three columns above that and takes its glyph from the page's own form icon, `toggle_on`.
 */
export function flagRow(group: FlagGroup): BoardRow {
  const first = group.versions[0];
  return {
    // The flag key, which is what this board's rows are unique by after grouping. Not prefixed with
    // a tenant: the row *is* every tenant's version of this key.
    id: group.flagKey,
    cells: [
      /*
       * Not mono, which is the design's own rule rather than an oversight.
       *
       * Its grid cell takes Geist Mono only for a value matching its prefix list — `ten_`, `evt_`,
       * `RPL`, `RSK`, `wf_`, `/v1`, `key`, `offset` — and `commerce_module` matches none of them, so
       * the design draws it in Geist at 600. The `Scope` cell below is `ten_…` on both sides and is
       * mono on both. This was mono here until the first capture was compared.
       */
      { text: group.flagKey },
      {
        text: scopeText(group),
        mono: true,
        title: `Held by ${String(group.versions.length)} tenant${group.versions.length === 1 ? "" : "s"}. CFG-02 models a flag per tenant and no platform-wide flag, so this column never reads "platform". Q-190.`,
      },
      { text: stateText(group) },
    ],
    state: stateOf(group),
    detail: [
      ...(first === undefined ? [] : [{ label: "Description", value: first.description }]),
      ...group.versions.map((feature) => ({
        label: feature.tenantId,
        value: `${perTenantState(feature)} · ${rolloutText(feature)} · ${audienceText(feature)}`,
        mono: true,
      })),
    ],
    /*
     * Two lines where the design's `History` block lists three, and the third is Q-107's.
     *
     * That block reads `Created`, `Last changed` and `Version` on every page of every one of these
     * design files, with a person's name beside the first two. No aggregate on this platform records
     * an actor a read can publish — CFG-02 records `requestedBy` and `approvedBy` and ADR 0045
     * withholds both as approval evidence — so the two entries here are the two a flag version
     * genuinely has. On a grouped row they are the newest version across the tenants that hold it,
     * which is said rather than implied.
     */
    history: newestVersionOf(group),
    glyph: "toggle_on",
  };
}

function newestVersionOf(group: FlagGroup): readonly { label: string; value: string }[] {
  const newest = [...group.versions].sort((left, right) =>
    right.publishedAt.localeCompare(left.publishedAt),
  )[0];
  if (newest === undefined) return [];
  return [
    { label: "Last published", value: `${asDay(newest.publishedAt)} · ${newest.tenantId}` },
    { label: "Version", value: `v${String(newest.version)}` },
  ];
}

/** The states this listing can actually answer, in the order `stateOf` decides them. */
export const FLAG_STATES: readonly string[] = ["armed", "rolling out", "on", "partial", "off"];

/** The states present on the rows the board is holding, so the filter offers nothing empty. */
export function statesPresent(groups: readonly FlagGroup[]): readonly string[] {
  const present = new Set(groups.map((group) => stateOf(group).label));
  return FLAG_STATES.filter((label) => present.has(label));
}

/**
 * What the board owes an operator about the fraction in its `State` column.
 *
 * Said rather than implied, because `on for 2 of 5` invites the stronger reading — 5 tenants on the
 * platform — and the weaker one is what the data supports. The sibling audit board took the same
 * decision in the same console for the same reason; see this file's header and Q-189.
 */
export function flagScopeNote(features: readonly FeatureSummary[] | undefined): string | undefined {
  if (features === undefined) return undefined;
  const tenants = new Set(features.map((feature) => feature.tenantId)).size;
  if (tenants === 0) return undefined;
  return `Flags from ${String(tenants)} tenant${tenants === 1 ? "" : "s"}. A fraction in the State column counts the tenants that hold that flag, not the tenants on the platform: nothing publishes how many tenants exist.`;
}

/**
 * Why the designed `New platform flag` form is drawn and refused.
 *
 * Three reasons, and any one of them is enough. The form's own fields are `Key`, `Scope`, `Tenants`
 * and `Kill switch`, and its primary is `Create flag`:
 *
 *   - **Both writes are `/internal/v1` only**, which the gateway does not expose —
 *     `POST /internal/v1/tenants/{tenantId}/features/{flagKey}/versions` and `…/kill-switch`.
 *     ADR 0053 publishes a read and nothing else, and §5 says why a cross-tenant write is a
 *     separate decision rather than an omission.
 *   - **A publication needs two different people.** CFG-02 refuses a proposal whose `requestedBy`
 *     equals the approving actor, and the form has no field for either, so one administrator cannot
 *     satisfy it at all. Q-151 records the same finding about the tenant console's own form.
 *   - **The form's `Scope` select offers `Platform`**, and CFG-02 models no platform-wide flag.
 *     Q-190.
 *
 * Drawn and disabled rather than removed, because `CLAUDE.md` section 1 says the add or edit form is
 * not to be removed, and rather than wired to something that would answer 404.
 */
export const CREATE_REFUSAL =
  "CFG-02 publishes the flag version write and the kill switch on /internal/v1 only, which the gateway does not expose, and a publication needs two different people — this form has a field for neither. Q-151, Q-190, ADR 0053 §5.";
